// src/hooks/useTranslation.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translateText, SUPPORTED_LANGUAGES } from '../utils/translationService';

/**
 * Custom hook for translating content
 * @param {string|object|array} initialContent - The initial content to translate
 * @returns {object} - Translation utility functions and state
 */
export const useTranslation = (initialContent = '') => {
  const { currentLanguage } = useLanguage();
  const [translatedContent, setTranslatedContent] = useState(initialContent);
  const [isTranslating, setIsTranslating] = useState(false);
  const translationCache = useRef(new Map());

  // Function to get cache key
  const getCacheKey = (text, langCode) => `${text}_${langCode}`;

  // Function to translate single text
  const translate = useCallback(async (text) => {
    if (!text || typeof text !== 'string' || text.trim() === '') {
      return text;
    }

    // Return original text if language is English (default)
    if (currentLanguage.code === SUPPORTED_LANGUAGES.ENGLISH.code) {
      return text;
    }

    // Check cache first
    const cacheKey = getCacheKey(text, currentLanguage.code);
    if (translationCache.current.has(cacheKey)) {
      return translationCache.current.get(cacheKey);
    }

    try {
      const translated = await translateText(text, currentLanguage.code);
      
      // Cache the result
      translationCache.current.set(cacheKey, translated);
      
      return translated;
    } catch (error) {
      console.error('Translation error:', error);
      return text; // Return original on error
    }
  }, [currentLanguage.code]);

  // Use effect to translate content when language changes
  useEffect(() => {
    let isMounted = true;

    const performTranslation = async () => {
      if (!initialContent) return;
      
      setIsTranslating(true);
      
      try {
        // Handle different content types
        let translated;
        
        if (typeof initialContent === 'string') {
          translated = await translate(initialContent);
        } 
        else if (Array.isArray(initialContent)) {
          // Handle array of strings
          const promises = initialContent.map(item => 
            typeof item === 'string' ? translate(item) : item
          );
          translated = await Promise.all(promises);
        }
        else if (typeof initialContent === 'object' && initialContent !== null) {
          // Handle object with string properties
          translated = { ...initialContent };
          const keys = Object.keys(initialContent);
          
          for (const key of keys) {
            if (typeof initialContent[key] === 'string') {
              translated[key] = await translate(initialContent[key]);
            }
          }
        } else {
          translated = initialContent;
        }
        
        if (isMounted) {
          setTranslatedContent(translated);
        }
      } catch (error) {
        console.error('Error in translation effect:', error);
      } finally {
        if (isMounted) {
          setIsTranslating(false);
        }
      }
    };

    performTranslation();

    return () => {
      isMounted = false;
    };
  }, [initialContent, currentLanguage, translate]);

  return {
    translated: translatedContent,
    isTranslating,
    translate, // Expose the translate function for on-demand translation
  };
};